/**
 * V149 — Unresolved Cities Panel
 *
 * Lists cities that fell outside the V147 backfill map — genuinely foreign
 * (Sydney, London, NYC, Dubai…) or Indian-typos (`bengluru`, "Delhi 110001",
 * etc.). Aggregated across registrations + inquiries + tutor_applications.
 *
 * Admin can:
 *  • see counts per source (Reg / Inq / Tut) and the latest occurrence
 *  • jump into any sample record via Universal Search (deep-link)
 *  • copy the city name to extend `_city_to_state` in `backend/invoices.py`
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, RefreshCw, Copy, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function UnresolvedCitiesPanel({ onOpenRecord }) {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({ total_unique_cities: 0, total_records: 0, map_size: 0, per_source_counts: {} });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q.trim()) params.append("q", q.trim());
        params.append("limit", "200");
        api.get(`/admin/unresolved-cities?${params}`)
            .then((r) => {
                setItems(r.data.items || []);
                setMeta(r.data);
            })
            .catch(() => toast.error("Failed to load unresolved cities"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const copyCity = (name) => {
        try { navigator.clipboard.writeText(name); toast.success(`Copied "${name}"`); }
        catch { toast.error("Clipboard blocked"); }
    };

    const totalDataPct = useMemo(() => {
        // rough coverage indicator: how many raw city records are still unresolved
        return meta.total_records ? Math.min(100, Math.round((meta.total_records / (meta.total_records + 452)) * 100)) : 0;
    }, [meta.total_records]);

    return (
        <div className="min-w-0" data-testid="unresolved-cities-panel">
            <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-heading font-bold text-[#0F172A] text-base sm:text-lg flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-rose-600" /> Unresolved Cities
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-0.5 max-w-md">
                            Cities that fell outside the V147 state map (foreign locations + Indian typos).
                            Extend <code className="text-[10px] bg-slate-100 rounded px-1">_city_to_state</code> in
                            <span className="text-[10px] font-mono"> backend/invoices.py</span> or fix the records via Universal Search.
                        </p>
                    </div>
                    <button onClick={load}
                        data-testid="unresolved-cities-reload"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-[#1E3A8A] text-slate-700 text-sm font-semibold rounded-lg">
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
                    </button>
                </div>

                {/* Summary chips */}
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Stat label="Unique cities" value={meta.total_unique_cities} testid="unresolved-cities-unique" />
                    <Stat label="Total records" value={meta.total_records} testid="unresolved-cities-records" />
                    <Stat label="Map size" value={meta.map_size} testid="unresolved-cities-map-size" hint="entries in _city_to_state" />
                    <Stat label="Unresolved share" value={`${totalDataPct}%`} testid="unresolved-cities-share" />
                </div>

                {/* Search */}
                <div className="mt-3 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter cities (e.g. lon, dubai, sf)…"
                        data-testid="unresolved-cities-filter"
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#1E3A8A] focus:outline-none" />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : items.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                    {q ? "No cities match your filter." : "Nothing unresolved — every city on file mapped to a state."}
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map((it) => (
                        <li key={it.city} className="px-4 sm:px-6 py-3" data-testid={`unresolved-city-row-${it.city}`}>
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-[#0F172A] truncate">{it.city}</span>
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-100 rounded-full px-1.5 py-0.5">
                                            {it.count} record{it.count === 1 ? "" : "s"}
                                        </span>
                                        {it.state_variants.length > 0 && (
                                            <span className="text-[10px] text-slate-500">
                                                stored state: {it.state_variants.join(" · ")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        Reg: <b>{it.sources.registration}</b> · Inq: <b>{it.sources.inquiry}</b> · Tut: <b>{it.sources.tutor}</b>
                                        {it.last_seen_at ? <span className="text-slate-400"> · last seen {it.last_seen_at.slice(0, 10)}</span> : null}
                                    </div>
                                    {it.samples?.length > 0 && (
                                        <div className="mt-1.5 flex flex-wrap gap-1">
                                            {it.samples.slice(0, 5).map((s) => (
                                                <button key={`${s.source}-${s.id}`} type="button"
                                                    onClick={() => onOpenRecord?.(s)}
                                                    data-testid={`unresolved-city-sample-${it.city}-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-2 py-0.5">
                                                    <ExternalLink className="w-2.5 h-2.5" /> {s.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => copyCity(it.city)}
                                    data-testid={`unresolved-city-copy-${it.city}`}
                                    title="Copy city name"
                                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E3A8A] bg-white border border-slate-200 hover:border-[#1E3A8A] px-2 py-1 rounded-md">
                                    <Copy className="w-3 h-3" /> Copy
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Stat({ label, value, testid, hint }) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500">{label}</div>
            <div className="text-lg font-heading font-bold text-[#0F172A] leading-tight">{value}</div>
            {hint && <div className="text-[9px] text-slate-400 mt-0.5">{hint}</div>}
        </div>
    );
}
